import { useEffect, useState } from "react";

import PropTypes from "prop-types";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import * as Yup from "yup";

import { yupResolver } from "@hookform/resolvers/yup";
import { Paper, Stack, Switch, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";

import { StatusEnum } from "../../shared";
import GeneralInformationSection from "./general-information-section";
import OutletAvailabilitySection from "./outlet-availability-section";
import ProductAddOnSection from "./product-add-on-section";
import ProductVariantsSection from "./product-variants-section";
import UploadTemplateSection from "./upload-template-section";
import VariantAttributesSection from "./variant-attributes-section";
import { useAuth } from "../../authentication";
import { PermissionEnum } from "../../permissions";

// Accept permission flags as arguments
const getValidationSchema = (
  variantsEnabled,
  canEditRetailPrice,
  canEditDistributorPrice
) =>
  Yup.object().shape({
    name: Yup.string().required("Harus diisi"),
    stockKeepingUnitPrefix: Yup.string().required("Harus diisi"),
    productCategory: Yup.object().required("Harus diisi"),
    unitOfMeasurement: Yup.object().required("Harus diisi"),
    status: Yup.boolean().required("Harus diisi"),
    retailPrice:
      !variantsEnabled && canEditRetailPrice
        ? Yup.number()
            .transform((value, originalValue) =>
              String(originalValue).trim() === "" ? undefined : value
            )
            .required("Harus diisi")
        : Yup.number().notRequired(),
    distributorPrice:
      !variantsEnabled && canEditDistributorPrice
        ? Yup.number()
            .transform((value, originalValue) =>
              String(originalValue).trim() === "" ? undefined : value
            )
            .required("Harus diisi")
        : Yup.number().notRequired(),
    variantAttributes: variantsEnabled
      ? Yup.array().of(
          Yup.object().shape({
            attribute: Yup.object().required("Harus diisi"),
            values: Yup.array()
              .min(1, "Minimal 1")
              .required("Nilai varian harus diisi"),
          })
        )
      : Yup.array().notRequired(),
    productAddOns: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Harus diisi"),
        additionalPrice: Yup.number()
          .transform((value, originalValue) =>
            String(originalValue).trim() === "" ? undefined : value
          )
          .required("Harus diisi"),
      })
    ),
  });

const DEFAULT_VALUES = {
  productCategory: null,
  unitOfMeasurement: null,
  status: StatusEnum.ACTIVE,
  productAddOns: [],
  variantAttributes: [],
  productVariants: [],
  availableToAllOutlets: false,
  outlets: {},
};

export const ProductForm = ({
  disableStockKeepingUnitPrefix = false,
  defaultValues,
  onSubmit,
  renderActionButton,
}) => {
  const { hasPermission } = useAuth();
  const [variantsEnabled, setVariantsEnabled] = useState(false);
  const validationSchema = getValidationSchema(
    variantsEnabled,
    hasPermission(PermissionEnum.UPDATE_PRODUCT_RETAIL_PRICE),
    hasPermission(PermissionEnum.UPDATE_PRODUCT_DISTRIBUTOR_PRICE)
  );

  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(validationSchema),
  });
  const { control, reset, handleSubmit, formState } = methods;

  const { append: appendVariantAttributes, remove: removeVariantAttributes } =
    useFieldArray({
      control,
      name: "variantAttributes",
    });

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    reset({ ...DEFAULT_VALUES, ...defaultValues });
    setVariantsEnabled(defaultValues.variantAttributes.length > 0);
  }, [defaultValues, reset]);

  const handleVariantEnabledChange = (e) => {
    const isChecked = e.target.checked;
    setVariantsEnabled(isChecked);
    if (isChecked) {
      appendVariantAttributes({ attribute: null, values: [] });
    } else {
      removeVariantAttributes();
    }
  };

  const onFormSubmit = async (data) => {
    try {
      let productVariants = [];
      if (data.variantAttributes.length === 0) {
        productVariants = [
          {
            sku: data.stockKeepingUnitPrefix,
            retailPrice: data.retailPrice,
            distributorPrice: data.distributorPrice,
            status: data.status,
          },
        ];
      } else {
        productVariants = data.productVariants.map((variant) => {
          const extractedVariant = {
            status: variant.status,
          };

          if (variant.retailPrice != null) {
            extractedVariant.retailPrice = variant.retailPrice;
          }

          if (variant.distributorPrice != null) {
            extractedVariant.distributorPrice = variant.distributorPrice;
          }

          if (variant.variantAttribute1Value) {
            extractedVariant.variantAttribute1Value =
              variant.variantAttribute1Value;
          }
          if (variant.variantAttribute2Value) {
            extractedVariant.variantAttribute2Value =
              variant.variantAttribute2Value;
          }
          if (variant.variantAttribute3Value) {
            extractedVariant.variantAttribute3Value =
              variant.variantAttribute3Value;
          }
          if (variant.variantAttribute4Value) {
            extractedVariant.variantAttribute4Value =
              variant.variantAttribute4Value;
          }

          return extractedVariant;
        });
      }

      const payload = {
        name: data.name,
        status: data.status,
        skuPrefix: data.stockKeepingUnitPrefix,
        productCategoryId: data.productCategory.id,
        unitOfMeasurementId: data.unitOfMeasurement.id,
        productVariants: productVariants,
        variantAttributes: data.variantAttributes.map((variantAttribute) => ({
          id: variantAttribute.attribute.id,
          values: variantAttribute.values,
        })),
        productAddOns: data.productAddOns,
        availableToAllOutlets: data.availableToAllOutlets,
        outletIds: data.availableToAllOutlets
          ? []
          : Object.keys(data.outlets).filter((key) => data.outlets[key]),
      };

      await onSubmit(payload);
    } catch (error) {
      // Optionally handle error here
      console.log(error.message);
    }
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        spacing={3}
      >
        <GeneralInformationSection
          disableStockKeepingUnitPrefix={disableStockKeepingUnitPrefix}
          variantsEnabled={variantsEnabled}
        />
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction="row" spacing={2} sx={{
            alignItems: "center"
          }}>
            <Typography variant="h6">Varian</Typography>
            <Switch
              checked={variantsEnabled}
              onChange={handleVariantEnabledChange}
            />
          </Stack>
          {variantsEnabled && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 8, lg: 9 }}>
                <VariantAttributesSection />
              </Grid>
              <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                <UploadTemplateSection />
              </Grid>
              <ProductVariantsSection />
            </Grid>
          )}
        </Paper>
        <ProductAddOnSection />
        <OutletAvailabilitySection />
        {renderActionButton({ isSubmitting: formState.isSubmitting })}
      </Stack>
    </FormProvider>
  );
};

ProductForm.propTypes = {
  disableStockKeepingUnitPrefix: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
};
